import {privateMediaUrl, fetchPrivateMedia} from './privateMedia';
const isMobileDevice = () => {
    const userAgent = navigator.userAgent || '';
    const isTouchMac = navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;

    return /Android|iPhone|iPad|iPod/i.test(userAgent) || isTouchMac;
};

const getAccessToken = () => {
    try {
        const user = JSON.parse(localStorage.getItem('user'));
        return user && user.accessToken ? user.accessToken : '';
    } catch (e) {
        return '';
    }
};

const getApiUrl = () => process.env.REACT_APP_API_URL || '';

export const getPdfExportProxyProps = () => ({
    forceProxy: isMobileDevice(),
    proxyURL: `${getApiUrl()}pdf-proxy`,
    proxyData: {
        token: getAccessToken()
    }
});

const PDF_EXPORT_LOADING_ID = 'pdf-export-loading-overlay';

const showPdfExportLoading = () => {
    if (typeof document === 'undefined' || document.getElementById(PDF_EXPORT_LOADING_ID)) {
        return;
    }

    const overlay = document.createElement('div');
    overlay.id = PDF_EXPORT_LOADING_ID;
    overlay.setAttribute('role', 'status');
    overlay.setAttribute('aria-live', 'polite');
    overlay.setAttribute('aria-label', 'Generovanie dokumentu');
    overlay.innerHTML = `
        <div class="pdf-export-loading-panel">
            <div class="pdf-export-loading-spinner"></div>
            <span>Generujem dokument...</span>
        </div>
    `;

    const style = document.createElement('style');
    style.id = `${PDF_EXPORT_LOADING_ID}-style`;
    style.textContent = `
        #${PDF_EXPORT_LOADING_ID} {
            position: fixed;
            inset: 0;
            z-index: 99999;
            display: flex;
            align-items: center;
            justify-content: center;
            background: rgba(247, 244, 238, 0.58);
            backdrop-filter: blur(4px);
            -webkit-backdrop-filter: blur(4px);
            pointer-events: all;
        }
        #${PDF_EXPORT_LOADING_ID} .pdf-export-loading-panel {
            min-width: 210px;
            padding: 24px 32px;
            border-radius: 14px;
            background: #fbfaf7;
            border: 1px solid rgba(176, 141, 87, 0.35);
            box-shadow: 0 18px 50px rgba(30, 53, 83, 0.22);
            color: #2c4767;
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 12px;
            font-weight: 600;
            letter-spacing: 0.2px;
        }
        #${PDF_EXPORT_LOADING_ID} .pdf-export-loading-spinner {
            width: 44px;
            height: 44px;
            border-radius: 50%;
            border: 4px solid rgba(176, 141, 87, 0.25);
            border-top-color: #b08d57;
            animation: pdf-export-spin 0.9s linear infinite;
        }
        @keyframes pdf-export-spin {
            to { transform: rotate(360deg); }
        }
    `;

    document.head.appendChild(style);
    document.body.appendChild(overlay);
};

const hidePdfExportLoading = () => {
    if (typeof document === 'undefined') {
        return;
    }

    const overlay = document.getElementById(PDF_EXPORT_LOADING_ID);
    const style = document.getElementById(`${PDF_EXPORT_LOADING_ID}-style`);

    if (overlay) {
        overlay.remove();
    }

    if (style) {
        style.remove();
    }
};

const getPdfRoot = (pdfRef) => pdfRef.current?.rootElForPDF || document;

const nextFrame = () => new Promise((resolve) => requestAnimationFrame(resolve));

const waitForImageLoad = (img) => new Promise((resolve, reject) => {
    if (img.complete && img.naturalWidth > 0) {
        resolve();
        return;
    }

    img.onload = () => resolve();
    img.onerror = () => reject(new Error(`Image failed to load: ${img.src}`));
});

const shouldEmbedImage = (src) => src && !src.startsWith('data:');

const fetchImageDataUrl = async (src) => {
    const response = privateMediaUrl(src) ? await fetchPrivateMedia(src) : await fetch(src, {cache: 'no-store'});
    if (!response.ok) throw new Error('Obrázok sa nepodarilo načítať.');
    const blob = await response.blob();
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
    });
};

const rasterizeDataUrl = async (dataUrl, width, height) => {
    const image = new Image();
    image.src = dataUrl;
    await waitForImageLoad(image);

    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, width || image.naturalWidth || image.width);
    canvas.height = Math.max(1, height || image.naturalHeight || image.height);

    const context = canvas.getContext('2d');
    context.drawImage(image, 0, 0, canvas.width, canvas.height);

    return canvas.toDataURL('image/png');
};

const embedImage = async (img) => {
    const originalSrc = img.dataset.privateSource || img.currentSrc || img.src;

    if (!shouldEmbedImage(originalSrc) || (img.dataset.pdfEmbedded === 'true' &&
        img.dataset.pdfOriginalSrc === originalSrc && img.src.startsWith('data:image/png'))) {
        return null;
    }

    const width = img.naturalWidth || img.width;
    const height = img.naturalHeight || img.height;
    const proxiedDataUrl = await fetchImageDataUrl(originalSrc);
    const pngDataUrl = await rasterizeDataUrl(proxiedDataUrl, width, height);

    img.dataset.pdfOriginalSrc = originalSrc;
    img.dataset.pdfEmbedded = 'true';
    img.setAttribute('src', pngDataUrl);
    img.src = pngDataUrl;
    await waitForImageLoad(img);

    return {
        originalSrc,
        embedded: img.src.startsWith('data:image/png'),
        width: img.naturalWidth,
        height: img.naturalHeight
    };
};

export const embedPdfImages = async (pdfRef) => {
    const root = getPdfRoot(pdfRef);
    const images = Array.from(root.querySelectorAll('img'));
    const results = [];

    for (const img of images) {
        const result = await embedImage(img);
        if (result) {
            results.push(result);
        }
    }

    await Promise.all(images.map((img) => {
        if (img.decode) {
            return img.decode().catch(() => undefined);
        }
        return waitForImageLoad(img).catch(() => undefined);
    }));

    await nextFrame();
    await nextFrame();

    if (process.env.NODE_ENV !== 'production') {
        console.table(images.map((img) => ({
            src: (img.src || '').slice(0, 80),
            embedded: img.src.startsWith('data:image/'),
            complete: img.complete,
            width: img.naturalWidth,
            height: img.naturalHeight
        })));
    }

    return results;
};

export const savePdfWithEmbeddedImages = async (pdfRef, callback) => {
    showPdfExportLoading();

    try {
        await embedPdfImages(pdfRef);

        if (!pdfRef.current) {
            return undefined;
        }

        return pdfRef.current.save(callback);
    } catch (error) {
        console.error('PDF export failed before save', error);
        if (callback) {
            callback(error);
        }
        return undefined;
    } finally {
        hidePdfExportLoading();
    }
};

export const exportPdfDataUriWithEmbeddedImages = async (pdfRef) => {
    showPdfExportLoading();

    try {
        await embedPdfImages(pdfRef);

        if (!pdfRef.current) {
            throw new Error('PDF export reference is not available.');
        }

        const {drawDOM, exportPDF} = await import('@progress/kendo-drawing');
        const options = pdfRef.current.getOptions ? pdfRef.current.getOptions() : pdfRef.current.props;
        const drawOptions = {
            avoidLinks: options.avoidLinks,
            forcePageBreak: options.forcePageBreak,
            keepTogether: options.keepTogether,
            margin: options.margin,
            paperSize: options.paperSize,
            landscape: options.landscape,
            repeatHeaders: options.repeatHeaders,
            scale: options.scale
        };
        const pdfOptions = {
            author: options.author,
            creator: options.creator || 'KendoReact PDF Generator',
            date: options.date,
            imgDPI: options.imageResolution,
            keywords: options.keywords,
            landscape: options.landscape,
            margin: options.margin,
            multiPage: true,
            paperSize: options.paperSize,
            producer: options.producer,
            subject: options.subject,
            title: options.title
        };

        const group = await drawDOM(pdfRef.current.rootElForPDF, drawOptions);
        return exportPDF(group, pdfOptions);
    } finally {
        hidePdfExportLoading();
    }
};
